// Single "next step" banner for the member dashboard. The dashboard used to
// stack up to three separate call-to-action banners (profile requirements,
// complete-profile, grow-goal prompt); this coordinator fetches all the
// signals and renders only the highest-priority one in a shared compact
// style, so members always see exactly one clear next step.
//
// Priority: accountability invitation (accept, then approve) → unread
// announcement → admin-published profile requirements → incomplete profile
// → open grow-goal prompt.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ClipboardList,
  Clock,
  Megaphone,
  Sparkles,
  Target,
  UserCircle,
  UserPlus,
  X,
} from 'lucide-react';
import { useAuth } from '../context/useAuth';
import {
  accountabilityGroupsAPI,
  growGoalPromptsAPI,
  notificationsAPI,
  profileRequirementsAPI,
  type NotificationEventItem,
} from '../api/client';
import type {
  AccountabilityPendingInvitations,
  GrowGoalPrompt,
  ProfileRequirement,
} from '../types';
import ProfileRequirementsForm from './ProfileRequirementsForm';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';

export default function DashboardActionBanner() {
  const { user } = useAuth();
  const [requirements, setRequirements] = useState<ProfileRequirement[]>([]);
  const [growGoalPrompt, setGrowGoalPrompt] = useState<GrowGoalPrompt | null>(null);
  const [accountabilityPending, setAccountabilityPending] =
    useState<AccountabilityPendingInvitations>({
      to_approve: [],
      to_accept: [],
    });
  const [modalOpen, setModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState<NotificationEventItem[]>([]);

  useEffect(() => {
    let active = true;
    profileRequirementsAPI
      .getMyUnmet()
      .then((data) => {
        if (active) setRequirements(data);
      })
      .catch(() => {
        // Non-blocking: if the check fails we simply don't show the banner.
      });
    growGoalPromptsAPI
      .getActive()
      .then((res) => {
        if (active) setGrowGoalPrompt(res.active ? res.prompt : null);
      })
      .catch(() => {
        // Non-blocking.
      });
    notificationsAPI
      .listEvents({ category: 'broadcast' })
      .then((rows) => {
        if (!active) return;
        const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
        setAnnouncements(
          rows.filter((row) => !row.read_at && new Date(row.created_at).getTime() > cutoff),
        );
      })
      .catch(() => {
        // Non-blocking.
      });
    return () => {
      active = false;
    };
  }, []);

  useVisibilityPolling({
    fetcher: accountabilityGroupsAPI.pendingInvitations,
    onSuccess: setAccountabilityPending,
    intervalMs: 8000,
    onError: () => {
      // Non-blocking: other dashboard actions remain available.
    },
  });

  const invitationToAccept = accountabilityPending.to_accept[0];
  if (invitationToAccept) {
    return (
      <BannerShell
        accent="border-green-500"
        icon={<UserPlus className="text-green-600" size={18} />}
        title={`Your invitation to ${invitationToAccept.group_name} is ready`}
        subtitle="Every current member approved. Review the invitation to accept or decline."
        action={
          <Link
            to="/accountability-groups"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition whitespace-nowrap"
          >
            Review invitation
            <ArrowRight size={14} />
          </Link>
        }
      />
    );
  }

  const invitationToApprove = accountabilityPending.to_approve[0];
  if (invitationToApprove) {
    return (
      <BannerShell
        accent="border-navy"
        icon={<Clock className="text-navy" size={18} />}
        title="An accountability invite needs your approval"
        subtitle={`${invitationToApprove.proposed_by_name} proposed inviting ${invitationToApprove.invitee_name} to ${invitationToApprove.group_name}. The invitee is not notified until everyone approves.`}
        action={
          <Link
            to="/accountability-groups"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 bg-gold text-white rounded-lg text-sm font-semibold hover:bg-gold-dark transition whitespace-nowrap"
          >
            Review request
            <ArrowRight size={14} />
          </Link>
        }
      />
    );
  }

  const announcement = announcements[0];
  if (announcement) {
    const remaining = announcements.length - 1;
    return (
      <BannerShell
        accent="border-navy"
        icon={<Megaphone className="text-navy" size={18} />}
        title={announcement.title}
        subtitle={announcement.body}
        action={
          <button
            type="button"
            onClick={() => {
              setAnnouncements((prev) => prev.filter((row) => row.id !== announcement.id));
              notificationsAPI.markEventRead(announcement.id).catch(() => undefined);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition whitespace-nowrap"
          >
            Dismiss
          </button>
        }
        footer={remaining > 0 ? `${remaining} more` : undefined}
      />
    );
  }

  if (requirements.length > 0) {
    return (
      <>
        <BannerShell
          accent="border-navy"
          icon={<ClipboardList className="text-navy" size={18} />}
          title="Iron Forums needs a bit more info from you"
          subtitle={`${
            requirements.length === 1 ? 'One quick item' : `${requirements.length} quick items`
          } to answer before your next snapshot.`}
          action={
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-dark transition whitespace-nowrap"
            >
              Finish now
              <ArrowRight size={14} />
            </button>
          }
        />

        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy">A bit more info, please</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Iron Forums uses this to serve you and your forum better.
                  </p>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
              <ProfileRequirementsForm
                requirements={requirements}
                onCompleted={() => {
                  setModalOpen(false);
                  setRequirements([]);
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (user && !user.profile_completed) {
    return (
      <BannerShell
        accent="border-navy"
        icon={<UserCircle className="text-navy" size={18} />}
        title="Complete your profile"
        subtitle="Help other members find and connect with you."
        action={
          <div className="flex items-center gap-2">
            <Link
              to="/jarvis?mode=onboarding"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-navy rounded-lg text-sm font-medium hover:bg-off-white transition whitespace-nowrap"
            >
              <Sparkles size={14} className="text-gold" />
              With Ask IF
            </Link>
            <Link
              to="/onboarding"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 bg-gold text-white rounded-lg text-sm font-semibold hover:bg-gold-dark transition whitespace-nowrap"
            >
              Complete profile
              <ArrowRight size={14} />
            </Link>
          </div>
        }
      />
    );
  }

  if (growGoalPrompt) {
    return (
      <BannerShell
        accent="border-navy"
        icon={<Target className="text-navy" size={18} />}
        title="Set your grow goal from today's Forum"
        subtitle={`Your ${growGoalPrompt.forum_name} facilitator asked everyone to set a grow goal.`}
        action={
          <Link
            to="/commitments?new=1"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 pointer-coarse:py-2.5 bg-gold text-white rounded-lg text-sm font-semibold hover:bg-gold-dark transition whitespace-nowrap"
          >
            Set grow goal
            <ArrowRight size={14} />
          </Link>
        }
      />
    );
  }

  return null;
}

function BannerShell({
  accent,
  icon,
  title,
  subtitle,
  action,
  footer,
}: {
  accent: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  action: React.ReactNode;
  footer?: string;
}) {
  return (
    <div className={`bg-white rounded-xl px-4 py-3 shadow-sm border-l-4 ${accent}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-off-white rounded-lg flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-navy leading-tight">{title}</h2>
            <p className="text-gray-500 text-xs leading-snug line-clamp-2">{subtitle}</p>
            {footer && <p className="text-[11px] text-gray-400 mt-1">{footer}</p>}
          </div>
        </div>
        <div className="ml-auto">{action}</div>
      </div>
    </div>
  );
}
